import * as Prism from 'prismjs';
import { CODE_LANGUAGES, CODE_SAMPLES, CodeLanguage } from '../../data/tool/code-image.data';
import { CODE_IMAGE_THEMES, CodeImageTheme } from '../../data/theme/code-image-themes.data';
import { CodeBackdrop, CodeImageOptions, CodeSpan, toLines } from './code-image.painter';

// What the code snippet widget is set to (2026-10-13), shared by the widget (its preview) and the hosts that draw
// more of the same picture (the tool page's full size picture, colour vision pictures and download).

/** the most lines one picture takes; a longer paste is cut here */
export const CODE_IMAGE_MAX_LINES = 300;
/** the most pixels a canvas is asked for (Safari's limit is near here) */
export const CODE_IMAGE_MAX_PIXELS = 16000000;

export interface CodeImageState {
  code: string;
  language: CodeLanguage;
  theme: CodeImageTheme;
  backdrop: CodeBackdrop;
  padding: number;
  lineNumbers: boolean;
  windowBar: boolean;
  title: string;
}

/** the widget as it opens: the TypeScript sample in Tokyo */
export function defaultCodeImageState(): CodeImageState {
  return {
    code: CODE_SAMPLES[ 'typescript' ],
    language: CODE_LANGUAGES[ 0 ],
    theme: CODE_IMAGE_THEMES.find( t => t.key === 'tokyo' ) || CODE_IMAGE_THEMES[ 0 ],
    backdrop: 'gradient',
    padding: 64,
    lineNumbers: false,
    windowBar: true,
    title: 'snippet.ts',
  };
}

/** The code as lines of coloured spans: Prism's tokens, or plain text until the grammar is here. */
function linesOf( code: string, language: string ): CodeSpan[][] {
  const text = code.replace( /\s+$/, '' ) || ' ';
  const grammar = ( Prism.languages as any )[ language ];
  return grammar ? toLines( Prism.tokenize( text, grammar ) ) : toLines( [ text ] );
}

/** whether the code is longer than one picture takes */
export function codeImageTruncated( state: CodeImageState ): boolean {
  return state.code.replace( /\s+$/, '' ).split( '\n' ).length > CODE_IMAGE_MAX_LINES;
}

/** The painter's options for the state at one pixel scale. */
export function codeImageOptions( state: CodeImageState, scale: number ): CodeImageOptions {
  return {
    theme: state.theme,
    lines: linesOf( state.code, state.language.key ).slice( 0, CODE_IMAGE_MAX_LINES ),
    padding: state.padding,
    fontSize: 14,
    lineNumbers: state.lineNumbers,
    backdrop: state.backdrop,
    windowBar: state.windowBar,
    title: state.title.trim(),
    scale,
  };
}
